import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, LogOut, Car, ChevronDown } from 'lucide-react';
import { useShadowMode } from '../contexts/ShadowModeContext';

/**
 * ResponsiveLayout - Mobile-first responsive layout with collapsible sidebar
 * 
 * Breakpoints:
 * - Mobile: 0-639px (sidebar hidden, hamburger menu)
 * - Tablet: 640-1023px (sidebar hidden, hamburger menu)
 * - Desktop: 1024px+ (sidebar fixed left)
 * 
 * Supports two sidebar formats:
 * 1. Flat list: sidebarItems = [{ id, icon, label }, ...]
 * 2. Grouped accordion: sidebarGroups = [{ title, icon, items: [...] }, ...]
 * 
 * Features:
 * - Swipe-from-left gesture support on mobile
 * - Auto-expands active section
 * - Shadow mode banner compatibility
 */

// Sidebar Group Component for collapsible sections
const SidebarGroup = ({ group, activeTab, onItemClick, expandedGroup, onToggleGroup }) => {
  const isExpanded = expandedGroup === group.title;
  const hasActiveItem = group.items.some(item => item.id === activeTab);
  const GroupIcon = group.icon;
  
  return (
    <div className="mb-1">
      {/* Group Header */}
      <button
        onClick={() => onToggleGroup(group.title)}
        className={`
          w-full flex items-center justify-between px-3 py-2.5 rounded-lg
          transition-all duration-200 text-xs font-semibold uppercase tracking-wider
          ${hasActiveItem 
            ? 'text-purple-300 bg-purple-500/10' 
            : 'text-white/50 hover:text-white/70 hover:bg-white/5'
          }
        `}
        data-testid={`group-${group.title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-2">
          {GroupIcon && <GroupIcon className="w-4 h-4" />}
          <span>{group.title}</span>
        </div>
        <ChevronDown 
          className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {/* Group Items - Animated collapse */}
      <div 
        className={`
          overflow-hidden transition-all duration-200 ease-out
          ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
        `}
      >
        <div className="pl-2 pt-1 space-y-0.5">
          {group.items.map((item) => (
            <button
              key={item.id}
              onClick={() => onItemClick(item)}
              className={`
                w-full flex items-center gap-3 px-3 py-2 rounded-lg
                transition-all duration-150 text-sm
                ${activeTab === item.id 
                  ? 'bg-purple-600/50 text-white' 
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
                }
              `}
              data-testid={`nav-${item.id}`}
            >
              {item.icon && <item.icon className="w-4 h-4 flex-shrink-0" />}
              <span className="font-medium truncate">{item.label}</span>
              {item.external && (
                <span className="ml-auto text-[10px] text-white/40">↗</span>
              )}
              {item.badge > 0 && (
                <span className="ml-auto w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center flex-shrink-0">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ResponsiveLayout = ({ 
  children, 
  sidebarItems = [],
  sidebarGroups = [], // NEW: Support grouped sidebar
  activeTab,
  onTabChange,
  headerTitle,
  headerBadge,
  headerIcon: HeaderIcon,
  headerGradient = 'from-slate-900 via-purple-900 to-slate-900',
  user,
  onLogout,
  sidebarHeader,
  sidebarFooter,
  bgClass = 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900',
  textClass = 'text-white'
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const navigate = useNavigate();
  
  // Shadow mode context for banner compatibility
  const { isInShadowMode } = useShadowMode();
  
  // Swipe gesture support - matching HostAppShell behavior
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const sidebarRef = useRef(null);

  // Determine if using grouped sidebar
  const useGroupedSidebar = sidebarGroups && sidebarGroups.length > 0;

  // Auto-expand the group containing the active tab
  useEffect(() => {
    if (useGroupedSidebar && activeTab) {
      const groupWithActiveItem = sidebarGroups.find(group => 
        group.items.some(item => item.id === activeTab)
      );
      if (groupWithActiveItem) {
        setExpandedGroup(groupWithActiveItem.title);
      }
    }
  }, [activeTab, sidebarGroups, useGroupedSidebar]);

  // Close sidebar on route change or tab change
  useEffect(() => {
    setSidebarOpen(false);
  }, [activeTab]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Swipe gesture handlers - consistent with HostAppShell
  useEffect(() => {
    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      if (!touchStartX.current || !touchStartY.current) return;
      
      const touchEndX = e.touches[0].clientX;
      const touchEndY = e.touches[0].clientY;
      
      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;
      
      // Only trigger if horizontal swipe is dominant
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        // Swipe right from left edge to open sidebar
        if (deltaX > 0 && touchStartX.current < 30 && !sidebarOpen) {
          setSidebarOpen(true);
          touchStartX.current = null;
          touchStartY.current = null;
        }
        // Swipe left to close sidebar
        if (deltaX < 0 && sidebarOpen) {
          setSidebarOpen(false);
          touchStartX.current = null;
          touchStartY.current = null;
        }
      }
    };

    const handleTouchEnd = () => {
      touchStartX.current = null;
      touchStartY.current = null;
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [sidebarOpen]);

  const handleNavClick = (item) => {
    if (item.href) {
      navigate(item.href);
    } else if (item.id && onTabChange) {
      onTabChange(item.id);
    }
    setSidebarOpen(false); // Auto-close on mobile after navigation
  };

  const handleToggleGroup = (groupTitle) => {
    setExpandedGroup(prev => prev === groupTitle ? null : groupTitle);
  };

  return (
    <div className={`min-h-screen ${bgClass} ${textClass} ${isInShadowMode ? 'pt-[68px]' : ''}`}>
      {/* Mobile Header - Only visible on mobile/tablet */}
      <header className={`lg:hidden fixed left-0 right-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10 px-4 py-3 safe-area-inset-top ${isInShadowMode ? 'top-[68px]' : 'top-0'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setSidebarOpen(true)} 
              className="text-white hover:bg-white/10"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </Button>
            {/* HireHub Logo - Always links to landing page */}
            <Link 
              to="/" 
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              data-testid="header-logo-link"
            >
              <div className="w-8 h-8 bg-[#004B8D] rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-sm sm:text-base text-white">
                HireHub
              </span>
            </Link>
          </div>
          {headerBadge}
        </div>
      </header>

      {/* Sidebar Backdrop - Mobile/Tablet only */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside 
        ref={sidebarRef}
        className={`
          fixed left-0 z-50 w-72 max-w-[85vw]
          bg-black/95 backdrop-blur-xl border-r border-white/10 
          transform transition-transform duration-300 ease-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isInShadowMode ? 'top-[68px] bottom-0' : 'inset-y-0'}
        `}
        data-testid="sidebar"
      >
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 lg:p-5 border-b border-white/10 flex items-center justify-between">
            {/* HireHub Logo - Always links to landing page */}
            <Link 
              to="/" 
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              data-testid="sidebar-logo-link"
            >
              <div className="w-10 h-10 bg-[#004B8D] rounded-xl flex items-center justify-center flex-shrink-0">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="font-heading font-bold text-base text-white">HireHub</h1>
                <p className="text-xs text-white/60">{headerTitle || 'Dashboard'}</p>
              </div>
            </Link>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden text-white hover:bg-white/10 flex-shrink-0" 
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* User Info */}
          {user && (
            <div className="px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-purple-600 rounded-full flex items-center justify-center text-sm flex-shrink-0">
                  {user.full_name?.charAt(0) || user.email?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{user.full_name}</p>
                  <p className="text-[11px] text-white/50 truncate">{user.email}</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation - Supports both flat and grouped */}
          <nav className="flex-1 p-3 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10">
            {useGroupedSidebar ? (
              // Grouped Accordion Navigation
              <div className="space-y-1">
                {sidebarGroups.map((group) => (
                  <SidebarGroup
                    key={group.title}
                    group={group}
                    activeTab={activeTab}
                    onItemClick={handleNavClick}
                    expandedGroup={expandedGroup}
                    onToggleGroup={handleToggleGroup}
                  />
                ))}
              </div>
            ) : (
              // Flat Navigation (backwards compatible)
              <div className="space-y-1">
                {sidebarItems.map((item) => (
                  <button
                    key={item.id || item.href}
                    onClick={() => handleNavClick(item)}
                    className={`
                      w-full flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-xl 
                      transition-all text-sm
                      ${activeTab === item.id 
                        ? 'bg-purple-600/50 text-white' 
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }
                    `}
                    data-testid={`nav-${item.id || item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.icon && <item.icon className="w-5 h-5 flex-shrink-0" />}
                    <span className="font-medium truncate">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="ml-auto w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center flex-shrink-0">
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </nav>

          {/* Sidebar Footer */}
          {sidebarFooter || (onLogout && (
            <div className="p-3 border-t border-white/10">
              <Button 
                variant="ghost" 
                className="w-full justify-start gap-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 h-10"
                onClick={onLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Logout</span>
              </Button>
            </div>
          ))}
        </div>
      </aside>

      {/* Main Content */}
      <div className={`lg:ml-72 min-h-screen ${isInShadowMode ? 'pt-[82px] lg:pt-[68px]' : 'pt-14 lg:pt-0'}`}>
        <div className="w-full max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * ResponsivePageHeader - Consistent page header for dashboard pages
 */
export const ResponsivePageHeader = ({ 
  title, 
  subtitle, 
  badge,
  actions,
  className = ''
}) => {
  return (
    <div className={`flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6 lg:mb-8 ${className}`}>
      <div className="min-w-0">
        <h1 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold truncate">{title}</h1>
        {subtitle && <p className="text-white/60 text-sm lg:text-base mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {badge}
        {actions}
      </div>
    </div>
  );
};

/**
 * ResponsiveStatsGrid - Responsive grid for stat cards
 */
export const ResponsiveStatsGrid = ({ children, className = '' }) => {
  return (
    <div className={`grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 ${className}`}>
      {children}
    </div>
  );
};

/**
 * ResponsiveCardsGrid - Responsive grid for action/info cards
 */
export const ResponsiveCardsGrid = ({ children, cols = { mobile: 1, tablet: 2, desktop: 3 }, className = '' }) => {
  return (
    <div className={`grid grid-cols-${cols.mobile} sm:grid-cols-${cols.tablet} lg:grid-cols-${cols.desktop} gap-3 sm:gap-4 lg:gap-6 ${className}`}>
      {children}
    </div>
  );
};

/**
 * MobileCardList - Converts table data to mobile-friendly cards
 */
export const MobileCardList = ({ 
  items, 
  renderCard, 
  emptyMessage = 'No items found',
  loading = false,
  className = ''
}) => {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <p className="text-center py-12 text-white/60">{emptyMessage}</p>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {items.map((item, index) => renderCard(item, index))}
    </div>
  );
};

export default ResponsiveLayout;
